import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { PromptApi, PromptApiList, PromptCreateApi } from '../Services/PromptApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';
import { PromptApiProvider, usePromptApi } from '../Services/PromptApiProvider';
import { formatTableTitle } from '../utils/formatTitle';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';

function PromptsHub({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getPrompts, deletePrompt, createPrompt } = usePromptApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<PromptApi[]>([]);
  const [totalPrompts, setTotalPrompts] = useState<number>(0);
  const [alertShown, setAlertShown] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<PromptApi[]>([]);
  const selectedId = selectedRows.length === 1 ? selectedRows[0].id : null;

  const tableTitle = 'Prompts';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'ID', selector: (row: PromptApi) => row.id, sortable: false },
    { name: 'Name', selector: (row: PromptApi) => row.name, sortable: false },
    {
      name: 'Created',
      selector: (row: PromptApi) => formatDateUTC(row.created_at),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: PromptApi) => formatDateUTC(row.updated_at),
      sortable: false,
    },
  ];

  // Display alert message on navigation
  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.returnMessage && !alertShown) {
      showAlert('help', location.state.returnMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, location.pathname]);

  const handleDelete = async (selectedId: string[]): Promise<void> => {
    if (!selectedId[0]) return;
    showConfirmation(`Are you sure you want to delete the selected prompt?`, async () => {
      try {
        setLoading(true);
        await deletePrompt(selectedId[0]);
        featchPrompts(persistedSkip, persistedPageSize);
        showAlert('success', 'Prompt was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the prompt');
      } finally {
        setLoading(false);
      }
    });
  };

  const createCopy = async (): Promise<void> => {
    if (!selectedId) return;
    try {
      setLoading(true);
      const promptToCopy = prompts.find((prompt) => prompt.id === selectedId);

      if (!promptToCopy) {
        showAlert('fail', 'Prompt not found');
        setLoading(false);
        return;
      }
      const baseName = promptToCopy.name;
      const copyNumbers = prompts
        .map((a) => {
          const match = a.name.match(new RegExp(`^${baseName} \\(Copy (\\d+)\\)$`));
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n) => n !== null);
      const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

      const newPromptData: PromptCreateApi = {
        name: `${baseName} (Copy ${nextCopyNumber})`,
        pattern_type: promptToCopy.pattern_type,
        definition: promptToCopy.definition,
      };
      await createPrompt(newPromptData);
      featchPrompts(persistedSkip, persistedPageSize);
      showAlert('success', 'Prompt copy was successfully created!');
    } catch (error) {
      showAlert('fail', 'Failed to create a copy of the prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClicked = (row: PromptApi): void => {
    navigate(`/organizations/${organizationId}/prompts-hub/${row.id}`);
  };

  const featchPrompts = async (skip: number, limit: number): Promise<void> => {
    setLoading(true);

    try {
      const response: PromptApiList = await getPrompts(skip, limit);
      setPrompts(response.items);
      setTotalPrompts(response.totalCount); // Set the total number of prompts
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setError(error.message); // Set error message
    } finally {
      setLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    featchPrompts(persistedSkip, persistedPageSize);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={prompts} // Render table only when data is available
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                paginationTotalRows={totalPrompts}
                disabled={false}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={featchPrompts}
                getRowUrl={(row) => `/organizations/${organizationId}/prompts-hub/${row.id}`}
                onSelectedRowsChange={({ selectedRows }) => {
                  setSelectedRows(selectedRows);
                }}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/prompts-hub/new-prompt`)
                  }
                >
                  Create New Prompt
                </Button>
              )}
              {selectedId && (
                <Button variant="ghost" size="medium" onClick={createCopy}>
                  Create copy
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PromptsHubWrapper({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <PromptApiProvider>
        <PromptsHub canEdit={canEdit} />
      </PromptApiProvider>
    </ConfirmationProvider>
  );
}

export default PromptsHubWrapper;
